import React, { useState, useEffect, useRef } from "react";
import { Box, Fade, Typography } from "@mui/material";
import { Notification, NotificationType } from "../types";
import { toNotificationType } from "../services/notificationService";
import animation1 from "../assets/animation1.webm";
import youcoin from "../assets/youcoin.png";

interface NotificationAlertProps {
  notification: Notification;
  onClose: () => void;
  overlaySize?: { width: string; height: string };
  animationConfig?: {
    type: NotificationType;
    theme: "light" | "dark";
    animationFile: string;
    duration: number;
  };
  theme?: "light" | "dark";
  donationTextTemplate?: string;
  challengeTextTemplate?: string;
  isPreview?: boolean; // Disable auto-close for preview mode
}

const NotificationAlert: React.FC<NotificationAlertProps> = ({
  notification,
  onClose,
  overlaySize = { width: "100vw", height: "100vh" },
  animationConfig,
  theme = "dark",
  donationTextTemplate = "{{sender}} donated {{amount}} for {{challenge}}",
  challengeTextTemplate = "{{sender}} challenged you to {{challenge}}",
  isPreview = false,
}) => {
  const [showAnimation, setShowAnimation] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [showText, setShowText] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const notificationType = toNotificationType(notification.notification_type);

  // Default animation configurations
  const defaultAnimations: Record<
    NotificationType,
    { file: string; duration: number }
  > = {
    challenge: { file: animation1, duration: 5000 },
    donation: { file: animation1, duration: 5000 },
    general: { file: animation1, duration: 5000 },
  };

  const currentAnimation = animationConfig || {
    type: notificationType,
    theme: theme,
    animationFile: defaultAnimations[notificationType].file,
    duration: defaultAnimations[notificationType].duration,
  };

  // Get the correct template based on notification type
  const getCurrentTemplate = () => {
    return notificationType === "donation"
      ? donationTextTemplate
      : challengeTextTemplate;
  };

  // Process text template with notification data (without coin image)
  const processTextTemplate = (template: string) => {
    return template
      .replace(/\{\{sender\}\}/g, notification.sender_name || "Anonymous")
      .replace(/\{\{amount\}\}/g, notification.coin_value?.toString() || "0")
      .replace(/\{\{challenge\}\}/g, notification.target_name || "Challenge");
  };

  // Check if template contains amount placeholder
  const hasAmountPlaceholder = (template: string) => {
    return template.includes("{{amount}}");
  };

  useEffect(() => {
    // Show animation when component mounts
    setShowAnimation(true);

    // Show text at 7 seconds
    const textTimer = setTimeout(() => {
      setShowText(true);
    }, 4700);

    // Auto-hide after animation duration (only if not in preview mode)
    if (!isPreview) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setShowText(false);
        setTimeout(onClose, 300); // Allow fade out animation
      }, currentAnimation.duration);

      return () => {
        clearTimeout(textTimer);
        clearTimeout(timer);
      };
    }

    return () => {
      clearTimeout(textTimer);
    };
  }, [currentAnimation.duration, onClose, isPreview]);

  // Ensure video plays when it loads
  useEffect(() => {
    if (videoRef.current && showAnimation) {
      const video = videoRef.current;

      const handleCanPlay = () => {
        video.play().catch((error) => {
          console.error("Error playing video:", error);
        });
      };

      const handleError = (error: any) => {
        console.error("Video error:", error);
      };

      // Handle video looping for preview mode
      const handleEnded = () => {
        if (isPreview) {
          video.currentTime = 0;
          video.play().catch((error) => {
            console.error("Error replaying video:", error);
          });
        }
      };

      video.addEventListener("canplay", handleCanPlay);
      video.addEventListener("error", handleError);
      video.addEventListener("ended", handleEnded);

      if (video.readyState >= 3) {
        video.play().catch((error) => {
          console.error("Error playing video immediately:", error);
        });
      }

      return () => {
        video.removeEventListener("canplay", handleCanPlay);
        video.removeEventListener("error", handleError);
        video.removeEventListener("ended", handleEnded);
      };
    }
  }, [showAnimation, currentAnimation.animationFile, isPreview]);

  const handleVideoEnd = () => {
    // Only hide animation if not in preview mode
    if (!isPreview) {
      setShowAnimation(false);
    }
  };

  return (
    <Fade in={isVisible} timeout={300}>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: overlaySize.width,
          height: overlaySize.height,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Animation video */}
        {showAnimation && (
          <Box
            sx={{
              width: "90%",
              height: "90%",
              display: "flex",
              justifyContent: "center",
              maxWidth: overlaySize.width,
              maxHeight: overlaySize.height,
              marginTop: "-120px",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              loop={false}
              playsInline
              onEnded={handleVideoEnd}
              style={{
                width: "100%",
                height: "100%",
                maxWidth: overlaySize.width,
                maxHeight: overlaySize.height,
                objectFit: "contain",
              }}
            >
              <source src={currentAnimation.animationFile} type="video/webm" />
              Your browser does not support the video tag.
            </video>


          </Box>
        )}

        {/* Custom text overlay that appears at 7 seconds */}
        {showText && (
          <Box
            sx={{
              position: "absolute",
              top: "86%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10000,
              animation: "textFadeIn 1s ease-in-out",
              "@keyframes textFadeIn": {
                from: {
                  opacity: 0,
                  transform: "translate(-50%, -50%) scale(0.9)",
                },
                to: {
                  opacity: 1,
                  transform: "translate(-50%, -50%) scale(1)",
                },
              },
            }}
          >
            <Box
               sx={{
                 color: "white",
                 textAlign: "center",
                 fontWeight: "bold",
                 fontSize: "2rem",
                 textShadow: "3px 3px 12px rgba(0, 0, 0, 1.0)",
                 padding: "20px 20px",
                 borderRadius: "15px",
                 width: 'auto',
                 minWidth: 'fit-content',
               }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1, width: 'auto', minWidth: 'fit-content', maxWidth: '90vw' }}>
                 {(() => {
                   const template = getCurrentTemplate();
                   const processedText = processTextTemplate(template);
                   
                   if (hasAmountPlaceholder(template)) {
                     // Replace {{amount}} with amount + coin icon as a single unit
                     const amountWithIcon = `${notification.coin_value?.toString() || "0"}`;
                     const textWithAmountIcon = processedText.replace(/\{\{amount\}\}/g, amountWithIcon);
                     
                     // Split by the amount value to separate text parts
                     const parts = textWithAmountIcon.split(amountWithIcon);
                     return (
                       <>
                         <Typography
                           component="span"
                           sx={{
                             fontFamily: "Montserrat",
                             fontWeight: "bold",
                             fontSize: 33,
                             color: "#F0F0F0",
                             textShadow: "3px 3px 8px rgba(0, 0, 0, 1.0)",
                             whiteSpace: 'nowrap',
                             display: 'inline'
                           }}
                         >
                           {parts[0]}
                         </Typography>
                         <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}>
                           <Typography
                             component="span"
                             sx={{
                               fontFamily: "Montserrat",
                               fontWeight: "bold",
                               fontSize: 33,
                               color: "#F0F0F0",
                               textShadow: "3px 3px 8px rgba(0, 0, 0, 1.0)",
                               whiteSpace: 'nowrap',
                               display: 'inline-block'
                             }}
                           >
                             {notification.coin_value?.toString() || "0"}
                           </Typography>
                           <img
                             src={youcoin}
                             alt="coin"
                             style={{
                               width: '30px',
                               height: '30px',
                               objectFit: 'contain',
                               filter: 'drop-shadow(3px 3px 8px rgba(0, 0, 0, 1.0))',
                               transform: 'translateY(+2px)',
                               display: 'inline-block',
                               verticalAlign: 'middle'
                             }}
                           />
                         </Box>
                         <Typography
                           component="span"
                           sx={{
                             fontFamily: "Montserrat",
                             fontWeight: "bold",
                             fontSize: 33,
                             color: "#F0F0F0",
                             textShadow: "3px 3px 8px rgba(0, 0, 0, 1.0)",
                             whiteSpace: 'nowrap',
                             display: 'inline'
                           }}
                         >
                           {parts[1]}
                         </Typography>
                       </>
                     );
                   } else {
                     // No amount placeholder, render normally
                     return (
                       <Typography
                         sx={{
                           fontFamily: "Montserrat",
                           fontWeight: "bold",
                           fontSize: 33,
                           color: "#F0F0F0",
                           textShadow: "3px 3px 8px rgba(0, 0, 0, 1.0)",
                           whiteSpace: 'nowrap'
                         }}
                       >
                         {processedText}
                       </Typography>
                     );
                   }
                 })()}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Fade>
  );
};

export default NotificationAlert;
